import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Button, Card, Form } from "react-bootstrap";
import { FaDownload } from "react-icons/fa";
import { toast } from "react-toastify";
import Message from "../../components/Message";
import Loader from "../../components/Loader";
import Meta from "../../components/Meta";
import {
  useGetCareerByIdQuery,
  useUpdateCareerNoteMutation,
} from "../../slices/submissionApiSlice";

const CareerDetailsScreen = () => {
  const { id } = useParams();
  const { data: career, isLoading, error, refetch } = useGetCareerByIdQuery(id);
  const [updateCareerNote, { isLoading: loadingUpdateNote }] =
    useUpdateCareerNoteMutation();

  const [adminNote, setAdminNote] = useState("");
  const [isEditingNote, setIsEditingNote] = useState(false);

  useEffect(() => {
    if (career?.adminNote) {
      setAdminNote(career.adminNote);
    }
  }, [career]);

  const saveNoteHandler = async () => {
    try {
      await updateCareerNote({ careerId: id, adminNote }).unwrap();
      refetch();
      setIsEditingNote(false);
      toast.success("Note saved successfully");
    } catch (err) {
      toast.error(err?.data?.message || err.message);
    }
  };

  const downloadFile = (fileUrl, fileName) => {
    const segments = String(fileUrl || "").split("/").filter(Boolean);
    const type = segments[1];
    const filename = segments[2];
    if (!type || !filename) {
      toast.error("File is not available");
      return;
    }

    fetch(
      `/api/files/download/${encodeURIComponent(type)}/${encodeURIComponent(
        filename,
      )}`,
      { credentials: "include" },
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error("Download failed");
        }
        return response.blob();
      })
      .then((blob) => {
        const fileURL = window.URL.createObjectURL(blob);
        const alink = document.createElement("a");
        alink.href = fileURL;
        alink.download = fileName.endsWith(".pdf")
          ? fileName
          : `${fileName}.pdf`;
        alink.click();
        window.URL.revokeObjectURL(fileURL);
      })
      .catch(() => {
        toast.error("Failed to download file");
      });
  };

  const formatPhoneForDisplay = (phone) => {
    if (!phone) return phone;
    const cleaned = phone.replace(/\D/g, "");

    // Only format exactly 10-digit US numbers
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
        6
      )}`;
    }

    // Return original for any other length
    return phone;
  };

  return (
    <>
      <Meta
        title="Career Application Details | Admin | Lugo Parts & Restoration"
        description="Admin panel for viewing details of career opportunities at Lugo Parts & Restoration."
      >
        <meta name="robots" content="noindex, nofollow" />
      </Meta>

      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error}</Message>
      ) : (
        <>
          <Card>
            <Card.Header>
              <h2>Career Application Details</h2>
            </Card.Header>
            <Card.Body>
              <h3>ID: {career._id}</h3>
              <h3>Name: {career.name}</h3>
              <h3>Email: {career.email}</h3>
              <h3>Phone: {formatPhoneForDisplay(career.phone)}</h3>
              <h3>Job Category: {career.jobCategory}</h3>
              <h3>
                Submitted On:{" "}
                {new Date(career.createdAt)
                  .toLocaleString("en-US", {
                    timeZone: "America/Los_Angeles",
                    hour12: false,
                  })
                  .substring(0, 19) + " (PT)"}
              </h3>
              {career.coverLetter && (
                <Button
                  variant="success"
                  className="me-2"
                  onClick={() =>
                    downloadFile(career.coverLetter, "careers-coverLetter")
                  }
                >
                  <FaDownload /> Download Cover Letter
                </Button>
              )}
              {career.resume && (
                <Button
                  variant="success"
                  onClick={() => downloadFile(career.resume, "careers-resume")}
                >
                  <FaDownload /> Download Resume
                </Button>
              )}
            </Card.Body>
          </Card>

          {/* Admin Note Section */}
          <Card className="mt-3">
            <Card.Header>
              <h3>Admin Note</h3>
            </Card.Header>
            <Card.Body>
              {isEditingNote ? (
                <>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={adminNote}
                    onChange={(e) => setAdminNote(e.target.value)}
                    placeholder="Add admin notes here..."
                  />
                  <Button
                    type="button"
                    className="btn btn-primary mt-2 me-2"
                    onClick={saveNoteHandler}
                    disabled={loadingUpdateNote}
                  >
                    {loadingUpdateNote ? "Saving..." : "Save Note"}
                  </Button>
                  <Button
                    type="button"
                    className="btn btn-secondary mt-2"
                    onClick={() => {
                      setAdminNote(career.adminNote || "");
                      setIsEditingNote(false);
                    }}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  <p
                    style={{
                      whiteSpace: "pre-wrap",
                      fontSize: "1.1rem",
                      marginBottom: "1rem",
                    }}
                  >
                    {adminNote || "No notes yet."}
                  </p>
                  <Button
                    type="button"
                    className="btn btn-primary mt-3"
                    onClick={() => setIsEditingNote(true)}
                  >
                    {adminNote ? "Edit Note" : "Add Note"}
                  </Button>
                </>
              )}
            </Card.Body>
          </Card>
        </>
      )}
    </>
  );
};

export default CareerDetailsScreen;
